
import { ApiResponse, ApiResponseFormError, ApiResponseLight, axios } from "@/libraries/axios";
import { session, settings } from "@/libraries/storage";
import { isUuid4 } from "@/router";
import { VeeTranslations, VeeValidateForm } from "@/stdlib/VeeValidateStdlib";
import { FormData, FormField, LandingPageRecord } from "@/views/Login/Interfaces";
import { includes, reduce } from "lodash";
import {
    extend,
    localize,
    setInteractionMode,
    ValidationObserver,
    ValidationProvider
} from "vee-validate";
import {
    alpha_num,
    alpha_spaces,
    email,
    numeric,
    required
} from "vee-validate/dist/rules";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { NavigationGuardNext, Route } from "vue-router/types/router";
import { namespace } from "vuex-class";

extend('alpha_num', alpha_num)
extend('alpha_spaces', alpha_spaces)
extend('email', email)
extend('numeric', numeric)
extend('required', required)
setInteractionMode('eager')

const AppStore = namespace('AppStore')

@Component({
    methods: {
        includes,
    },
    components: {
        ValidationObserver,
        ValidationProvider,
    },
})
export default class LandingPageView extends Vue {
    @Prop({
        type: String
    }) readonly landingPageId!: string | null

    @AppStore.State
    appLanguage!: string | null

    title: string | null = null

    beforeRouteEnter({params}: Route, from: Route, next: NavigationGuardNext): void {
        if (params.landingPageId) {
            isUuid4(params.landingPageId) ? next() : next(false)
        } else {
            next()
        }
    }

    beforeMount(): void {
        if (this.appLanguage) {
            localize(this.appLanguage, VeeTranslations[this.appLanguage])
        }
    }

    created(): void {
        this.loader()
    }

    form: VeeValidateForm | null = null

    get formData(): FormData[] {
        return reduce(this.formMapping, (formData, field) => {
            formData.push({
                id: field.id,
                label: field.label,
                name: field.name,
                type: field.type,
                value: this.formValue[field.name],
            })

            return formData
        }, [] as FormData[])
    }

    formMapping: FormField[] | null = null

    formSubmit(): Promise<boolean> {
        return new Promise(resolve => {
            this.form?.validate()
                .then(formValid => {
                    if (formValid) {
                        const data: Record<string, FormData[] | string> = {
                            formData: this.formData,
                        }

                        if (this.user) {
                            data.strategist = this.user.strategist
                            data.strategistId = this.user.strategistId
                        }

                        const worker = axios.post<ApiResponseLight | ApiResponseFormError>(`/v1/landing-page/${this.landingPageId}`, data)
                        worker.then(({data, status}) => {
                            if ((status === 200) && (data.code === 201)) {
                                if (this.user) {
                                    settings.set('auth', 'user', 'token', this.user.accessToken)
                                    settings.set('auth', 'user', 'strategist', this.user.strategist)

                                    session.del('auth', 'user')
                                }

                                this.$router.push('/app').catch(() => { /** Silence! I know what I do! */ })

                                resolve(true)
                            } else {
                                resolve(false)
                            }
                        })
                    } else {
                        resolve(false)
                    }
                })
        })
    }

    formValue: Record<string, string | number | null> = {}

    loader(): Promise<LandingPageRecord | null> {
        return axios.get<ApiResponse<LandingPageRecord>>(`/v1/landing-page/${this.landingPageId}`)
            .then(({data, status}) => {
                if ((status === 200) && (data.code === 302)) {
                    this.formMapping = data.pyld.formMapping
                    this.title = data.pyld.title

                    this.formValue = reduce(data.pyld.formMapping, (values, field) => {
                        values[field.name] = field.default || null

                        return values
                    }, {} as Record<string, string | number | null>)

                    return data.pyld
                }

                return null
            })
            .catch(reason => {
                console.error(reason)

                return null
            })
    }

    get user(): {
        accessToken: string
        strategist: string
        strategistId: string
    } | null {
        return session.get('auth', 'user')
    }

    @Watch('appLanguage')
    appLanguage_(appLanguage: string): void {
        localize(appLanguage, VeeTranslations[appLanguage])
    }
}
